import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Button } from '@cubeyou-team/hx-react-components';
import styled from 'styled-components';

import { isEmpty } from 'src/utils';
import {
  requestRepository,
  requestFolder,
  exploreFolder,
  showModal,
  showEditAudienceModal,
  loadMoreRepository,
  sendEvent,
} from '../../actions';
import { getCurrentTreeView } from '../../selectors/respository';
import {
  NewOrSearchBox,
  FoldersNavigation,
  SearchResultsCounter,
} from '../../components';
import HeaderContainer from '../HeaderContainer';

import getItemActions from '../../itemActions';
import RepositoryToggleView from '../../components/RepositoryToggleView';
import { getCurrentProductDataSources } from '../../selectors/myCompany';
import { useMRISimmons } from '../../modules/audience-activation/hooks/useMriSimmons';
import { AudienceActivationModal } from '../../modules/audience-activation/components/AudienceActivationModal';

const StyledRespositoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledRespositoryListContainer = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const RepositoryContainer = ({
  repository,
  currentTreeView,
  repositorySize,
  onItemDragToggle,
  onViewChange,
  requestRepository,
  requestFolder,
  exploreFolder,
  showModal,
  showEditAudienceModal,
  loadMoreRepository,
  sendEvent,
}) => {
  const productDataSources = useSelector(state =>
    getCurrentProductDataSources(state)
  );
  const isMyCompanyLoading = useSelector(state => state.myCompany.isLoading);

  const [q, setQ] = useState('');
  const { isMriSimmonsActivated } = useMRISimmons();

  useEffect(() => {
    if (!isEmpty(productDataSources) && !isMyCompanyLoading) {
      requestRepository('twitter');
    }
    if (isEmpty(productDataSources) && !isMyCompanyLoading) {
      requestRepository('recent');
    }
  }, [productDataSources, requestRepository, isMyCompanyLoading]);

  const handleOnFilterChange = useCallback(
    filter => {
      sendEvent('Workspace');
      requestRepository(filter);
    },
    [sendEvent, requestRepository]
  );

  const handleOnSearchChange = useCallback(
    q => {
      sendEvent('Workspace');
      setQ(q);

      if (q === '') {
        requestRepository(repository.filter);
      } else {
        requestRepository('all', q);
      }
    },
    [repository.filter, requestRepository, sendEvent]
  );

  const handleOnNewFolderCreate = useCallback(() => {
    showModal(
      {
        isOpen: true,
        position: 'center',
      },
      'CreateFolder'
    );

    sendEvent('Workspace');
  }, [showModal, sendEvent]);

  const handleOnFolderRequest = useCallback(
    ({ id }, path, loaded) => {
      if (!loaded) {
        return requestFolder(id);
      }

      if (id === 0) {
        exploreFolder(0);
      } else {
        exploreFolder(id);
      }
    },
    [requestFolder, exploreFolder]
  );

  const isSearching = q.length > 0;
  const repositoryItemActions = getItemActions({
    showModal,
    showEditAudienceModal,
  });
  return (
    <StyledRespositoryContainer>
      <HeaderContainer size={repositorySize} />
      {repositorySize !== 'collapsed' && (
        <>
          <NewOrSearchBox
            search={q}
            filter={repository.filter}
            onFilterChange={handleOnFilterChange}
            onSearchChange={handleOnSearchChange}
            onNewFolderCreate={handleOnNewFolderCreate}
          />
          {isSearching && (
            <SearchResultsCounter count={currentTreeView.length} />
          )}
          {repository.filter === 'my' && repositorySize !== 'full' && (
            <Box
              pL="small"
              borderSize="1px"
              borderPosition="bottom"
              style={{
                height: '50px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                icon="add"
                onClick={handleOnNewFolderCreate}
                size="small"
                variant="subtle"
              >
                New Folder
              </Button>
            </Box>
          )}
          <StyledRespositoryListContainer>
            <FoldersNavigation
              isInitialLoading={
                repository.isLoading || repository.isLoadingFolders
              }
              size={repositorySize}
              shouldCallLoadMore={repository.shouldCallLoadMore}
              treeView={currentTreeView}
              filter={repository.filter}
              initialPath={repository.path}
              hideActions={false}
              onNavigationClick={handleOnFolderRequest}
              onLoadMoreRequest={loadMoreRepository}
              onItemDragToggle={onItemDragToggle}
              actions={repositoryItemActions}
            />
          </StyledRespositoryListContainer>
        </>
      )}
      {repositorySize !== 'full' && (
        <RepositoryToggleView
          style={{
            top: `112px`,
          }}
          checked={repositorySize === 'default'}
          label={repositorySize === 'default' ? 'Collapse' : 'Expand'}
          onClick={checked => onViewChange(!checked ? 'collapsed' : 'default')}
        />
      )}
      {repositorySize !== 'collapsed' && (
        <RepositoryToggleView
          style={{
            top: `142px`,
          }}
          checked={repositorySize === 'full'}
          label={repositorySize === 'full' ? 'Default view' : 'Full view'}
          onClick={checked => onViewChange(!checked ? 'default' : 'full')}
        />
      )}
      <AudienceActivationModal />
    </StyledRespositoryContainer>
  );
};

const mapDispatchToProps = {
  requestRepository,
  requestFolder,
  exploreFolder,
  showModal,
  showEditAudienceModal,
  loadMoreRepository,
  sendEvent,
};

const mapStateToProps = state => ({
  repository: state.repository,
  currentTreeView: getCurrentTreeView(state),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RepositoryContainer);
